import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkGrid from "@/components/feature/WorkGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import { getRelated, getWorkById } from "@/utils/work";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

const TYPE_LABEL: Record<string, string> = {
  movie: "电影",
  tvseries: "电视剧",
};

export default function Detail() {
  const { id = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const { isFavorite, toggleFavorite } = useFavorites();

  const work = getWorkById(id);

  usePageMeta(
    work ? `${work.title} | 韩国伦理在线观看` : "作品不存在 | 韩国伦理在线观看",
    work ? work.synopsis.slice(0, 70) : "该作品信息不存在或已下架。",
  );

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 350);
    return () => window.clearTimeout(timer);
  }, [id]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full px-4 py-10 md:px-8">
          <div className="skeleton-shimmer animate-shimmer h-72 w-full rounded-lg" />
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <div className="skeleton-shimmer animate-shimmer h-6 w-1/3 rounded" />
              <div className="skeleton-shimmer animate-shimmer h-24 w-full rounded" />
            </div>
            <div className="skeleton-shimmer animate-shimmer h-48 w-full rounded-lg" />
          </div>
        </div>
      </SiteLayout>
    );
  }

  if (!work) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-8">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-secondary-100 text-4xl text-secondary-500">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">
            作品信息不存在或已下架
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            你访问的影视作品可能已经下架或链接有误，返回首页看看其他精彩内容吧。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
            <Link
              to="/category/all"
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300"
            >
              浏览全部作品
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelated(work, 5);
  const favorited = isFavorite(work.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("分享链接已复制到剪贴板");
    } catch {
      setToast("分享链接复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <SiteLayout>
      {/* hero */}
      <div className="relative w-full overflow-hidden">
        <img
          src={work.backdrop}
          alt={`${work.title} 主视觉`}
          title={`${work.title} 韩国伦理影视作品`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/55" />

        <div className="relative mx-auto w-full px-4 py-8 md:px-8">
          <Breadcrumb
            tone="light"
            items={[
              { label: "首页", to: "/" },
              { label: TYPE_LABEL[work.type], to: `/category/${work.type}` },
              { label: work.title },
            ]}
          />
          <div className="mt-7">
            <div className="flex flex-wrap items-end gap-6">
              <div
                className="hidden w-40 shrink-0 overflow-hidden rounded-lg border border-background-50/20 sm:block"
                style={{ aspectRatio: "2 / 3" }}
              >
                <img
                  src={work.poster}
                  alt={`${work.title} 海报`}
                  title={`${work.title} 韩国伦理影视作品封面`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="max-w-2xl">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-accent-500 px-2.5 py-1 text-[11px] font-bold text-background-50">
                    {TYPE_LABEL[work.type]}
                  </span>
                  {work.genres.map((genre) => (
                    <span
                      key={genre}
                      className="rounded-full border border-background-50/30 px-2.5 py-1 text-[11px] text-background-100"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
                <h1 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-4xl">
                  {work.title}
                </h1>
                <p className="mt-1.5 text-sm text-background-300">
                  {work.originalTitle} · {work.year} · {work.duration}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-background-200">
                  <span className="flex items-center gap-1 font-semibold text-accent-300">
                    <i className="ri-star-fill text-sm" />
                    {work.rating.toFixed(1)} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-sm" />
                    热度 {work.popularity}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line text-sm" />
                    更新于 {work.updateDate}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* body */}
      <div className="mx-auto w-full px-4 py-10 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{work.synopsis}</p>
            </section>

            <section className="mt-9">
              <h2 className="font-heading text-lg font-bold text-foreground-950">演职人员</h2>
              <div className="mt-4 flex flex-wrap gap-5">
                <div className="flex w-24 flex-col items-center text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-lg font-bold text-primary-700">
                    {work.director.slice(0, 1)}
                  </span>
                  <span className="mt-2 text-sm font-semibold text-foreground-900">
                    {work.director}
                  </span>
                  <span className="text-xs text-foreground-500">导演</span>
                </div>
                {work.cast.map((actor) => (
                  <div key={actor} className="flex w-24 flex-col items-center text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-lg font-bold text-secondary-700">
                      {actor.slice(0, 1)}
                    </span>
                    <span className="mt-2 text-sm font-semibold text-foreground-900">{actor}</span>
                    <span className="text-xs text-foreground-500">主演</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">作品信息</h3>
              <dl className="mt-4 space-y-3 text-sm">
                {[
                  { label: "类别", value: TYPE_LABEL[work.type] },
                  { label: "年份", value: String(work.year) },
                  { label: "时长", value: work.duration },
                  { label: "导演", value: work.director },
                  { label: "主演", value: work.cast.join("、") },
                  { label: "更新日期", value: work.updateDate },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between gap-4">
                    <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="text-right font-medium text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <button
              type="button"
              onClick={() => setToast("本站仅提供作品信息展示，暂不提供在线播放")}
              className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-lg" />
              立即观看（暂不可用）
            </button>
            <p className="text-center text-xs text-foreground-500">
              演示站点不含在线播放功能，仅作信息展示
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  const nowFav = toggleFavorite(work.id);
                  setToast(nowFav ? "已加入我的收藏" : "已取消收藏");
                }}
                className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors ${
                  favorited
                    ? "border-accent-300 bg-accent-100 text-accent-700"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-300"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300"
              >
                <i className="ri-share-line text-base" />
                分享
              </button>
            </div>

            <Link
              to={`/category/${work.type}`}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
            >
              <i className="ri-arrow-left-line text-base" />
              返回{TYPE_LABEL[work.type]}列表
            </Link>
          </aside>
        </div>

        <section className="mt-14">
          <SectionHeading
            eyebrow="相关推荐"
            title="同类韩国伦理影视推荐"
            description="根据题材标签为你匹配的相似作品，喜欢这部就别错过它们。"
          />
          <WorkGrid works={related} skeletonCount={5} columns={5} />
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-md bg-foreground-950 px-5 py-3 text-sm font-medium text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}